import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './services/authConfig';
import Header from './Components/Header/Header';
import Login from './Pages/Login';
import Ams from './Pages/Ams';
import AssetVerification from './Pages/AssetVerification';
import AssestDraft from './Pages/AssestDraft';
import AssetVerificationReport from './Pages/AssetVerificationReport';
import VerifiedAssets from './Pages/VerifiedAssets';
import ResultsPageNew from './Pages/ResultsPageNew';
import AssetPicker from './Pages/AssetPicker';
import AssetDetailPopup from './Pages/AssetDetailPopup';
import api from './services/api';
import './App.css';

const msalInstance = new PublicClientApplication(msalConfig);

class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('UI Error:', error.message, info.componentStack); }
  render() {
    if (this.state.hasError) {
      return <div style={{ padding: '2rem', textAlign: 'center' }}>Something went wrong. Please refresh the page.</div>;
    }
    return this.props.children;
  }
}

// Protected Route Component
function ProtectedRoute({ children, user, allowedRoles }) {
  if (allowedRoles && !allowedRoles.includes(user.role_code)) {
    return <Navigate to="/" />;
  }
  return children;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsVerification, setNeedsVerification] = useState(false);

  useEffect(() => {
    if (window.location.pathname === '/asset-picker') {
      setLoading(false);
      return;
    }

    const restoreSession = async () => {
      try {
        // Try restoring session from httpOnly cookie via /auth/me
        const { data } = await api.get('/auth/me');
        setUser(data);
        checkVerificationNeeded(data);
        setLoading(false);
        return;
      } catch {
        // No valid session cookie — fall through to SSO redirect handling
      }

      // Handle AAD redirect response
      try {
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
          const userId = response.account.username.split('@')[0];
          const { data } = await api.post('/auth/sso-login', { user_id: userId });
          setUser(data);
          await checkVerificationNeeded(data);
        }
      } catch (err) {
        console.error('SSO Error:', err.message);
        alert(err.response?.data?.error || 'SSO login failed. User not authorized.');
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const checkVerificationNeeded = async (userData) => {
    try {
      const { data } = await api.get(`/user/${userData.user_id}`);
      const assets = Array.isArray(data) ? data : [];
      const hasUnverified = assets.some(asset => {
        const verification = asset.verification || {};
        const sortedKeys = Object.keys(verification).sort((a, b) =>
          parseInt(a.split('_')[1]) - parseInt(b.split('_')[1])
        );
        const latestKey = sortedKeys[sortedKeys.length - 1];
        const latestVerification = latestKey ? verification[latestKey] : null;
        return !latestVerification || (latestVerification.accepted !== '+' && latestVerification.accepted !== '-');
      });
      setNeedsVerification(hasUnverified);

      if (hasUnverified) {
        alert(`Users are requested to verify all the assets which are on their name.
Re-check the Assets which are still not verified.
Non-Verified Asstes will be removed from network.
Email ID on which that Asset is assigned will get blocked.`);
        alert(`User has the facility to choose 'Select All' option to select all assets and verify`);
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        return;
      }
      console.error('Error checking verification:', err);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
    checkVerificationNeeded(userData);
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // proceed with local logout even if server call fails
    }
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
    setUser(null);
    window.location.href = '/';
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // Allow asset picker and asset details to render without authentication
  if (window.location.pathname === '/asset-picker' || window.location.pathname.startsWith('/asset-details')) {
    return (
      <Router>
        <Routes>
          <Route path="/asset-picker" element={<AssetPicker />} />
          <Route path="/asset-details/:assetNo" element={<AssetDetailPopup />} />
        </Routes>
      </Router>
    );
  }

  // Handle MSAL popup callback
  if (window.opener && window.location.hash) {
    return <MsalProvider instance={msalInstance}><div></div></MsalProvider>;
  }

  if (!user) {
    return (
      <MsalProvider instance={msalInstance}>
        <Login onLogin={handleLogin} />
      </MsalProvider>
    );
  }

  const isUser = user.role_code === 'user';

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/asset-picker" element={<AssetPicker />} />
          <Route path="/asset-details/:assetNo" element={<AssetDetailPopup />} />
          <Route path="/results" element={<ResultsPageNew />} />
          <Route path="/" element={
            needsVerification
              ? <Navigate to="/asset-verification" replace />
              : <>
                <Header user={user} onLogout={handleLogout} />
                <Ams user={user} />
              </>
          } />
          <Route path="/asset-verification" element={
            <>
              <Header user={user} onLogout={handleLogout} />
              <AssetVerification user={user} />
            </>
          } />
          <Route path="/asset-draft" element={
            <>
              <Header user={user} onLogout={handleLogout} />
              <AssestDraft user={user} />
            </>
          } />
          <Route path="/verified-assets" element={
            <>
              <Header user={user} onLogout={handleLogout} />
              <VerifiedAssets user={user} />
            </>
          } />
          <Route
            path="/asset-report"
            element={
              <ProtectedRoute user={user} allowedRoles={['admin', 'manager', 'DivAdmin']}>
                <Header user={user} onLogout={handleLogout} />
                <AssetVerificationReport />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default function WrappedApp() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}
